// utils/routeValidation.js
import { ROUTE_CONFIG } from '../config/routeConfig';

/**
 * Validate route data structure
 */
export const validateRouteData = (routeData) => {
  const errors = [];
  
  if (!routeData) {
    errors.push('Route data is missing');
    return { isValid: false, errors };
  }
  
  // Check required fields
  if (!Array.isArray(routeData.routes)) {
    errors.push('Routes array is missing or invalid');
  }
  
  if (!Array.isArray(routeData.sites)) {
    errors.push('Sites array is missing or invalid');
  }
  
  // Validate individual routes
  routeData.routes?.forEach((dayRoute, index) => {
    if (!dayRoute.day) {
      errors.push(`Route at index ${index} is missing day`);
    }
    
    if (!Array.isArray(dayRoute.routes)) {
      errors.push(`Route at index ${index} has invalid routes array`);
    }
  });
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Validate stop data
 */
export const validateStop = (stop) => {
  const errors = [];
  
  if (!stop.siteId) {
    errors.push('Stop is missing siteId');
  }
  
  if (!stop.latitude || !stop.longitude) {
    if (!stop.location?.coordinates?.length === 2) {
      errors.push('Stop is missing valid coordinates');
    }
  }
  
  if (typeof stop.estimatedHours !== 'number' || stop.estimatedHours < 0) {
    errors.push('Stop has invalid estimated hours');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Validate coordinates
 */
export const validateCoordinates = (lat, lng) => {
  if (typeof lat !== 'number' || typeof lng !== 'number') {
    return { isValid: false, error: 'Coordinates must be numbers' };
  }
  
  if (lat < -90 || lat > 90) {
    return { isValid: false, error: 'Latitude must be between -90 and 90' };
  }
  
  if (lng < -180 || lng > 180) {
    return { isValid: false, error: 'Longitude must be between -180 and 180' };
  }
  
  // Validate they're in Arizona area (rough bounds)
  if (lat < 31 || lat > 37 || lng < -115 || lng > -109) {
    return { isValid: false, error: 'Coordinates are outside service area' };
  }
  
  return { isValid: true };
};

/**
 * Validate assignment data
 */
export const validateAssignmentData = (assignmentData) => {
  const errors = [];
  
  if (!assignmentData.siteId) {
    errors.push('Site ID is required');
  }
  
  if (!assignmentData.crewId) {
    errors.push('Crew ID is required');
  }
  
  if (!assignmentData.selectedDate) {
    errors.push('Date is required');
  }
  
  if (assignmentData.scheduledDates && typeof assignmentData.scheduledDates !== 'object') {
    errors.push('Scheduled dates must be an object');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Validate job site data
 */
export const validateJobSite = (site) => {
  const errors = [];
  
  if (!site._id) {
    errors.push('Site ID is missing');
  }
  
  if (!site.name) {
    errors.push('Site name is missing');
  }
  
  if (!site.frequency) {
    errors.push('Site frequency is missing');
  }
  
  const coordsValidation = validateCoordinates(
    site.latitude || site.location?.coordinates?.[1],
    site.longitude || site.location?.coordinates?.[0]
  );
  
  if (!coordsValidation.isValid) {
    errors.push(coordsValidation.error);
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Validate route optimization input
 */
export const validateOptimizationInput = (sites) => {
  const errors = [];
  
  if (!Array.isArray(sites)) {
    errors.push('Sites must be an array');
    return { isValid: false, errors };
  }
  
  if (sites.length === 0) {
    errors.push('No sites to optimize');
    return { isValid: false, errors };
  }
  
  if (sites.length > ROUTE_CONFIG.OPTIMIZATION.MAX_STOPS_PER_ROUTE) {
    errors.push(`Too many stops. Maximum is ${ROUTE_CONFIG.OPTIMIZATION.MAX_STOPS_PER_ROUTE}`);
  }
  
  sites.forEach((site, index) => {
    const validation = validateStop(site);
    if (!validation.isValid) {
      errors.push(`Site at index ${index}: ${validation.errors.join(', ')}`);
    }
  });
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Sanitize route data for API
 */
export const sanitizeRouteData = (data) => {
  // Remove any undefined or null values
  const cleaned = {};
  
  Object.keys(data).forEach(key => {
    if (data[key] !== undefined && data[key] !== null) {
      cleaned[key] = data[key];
    }
  });
  
  // Ensure required fields have defaults
  return {
    ...cleaned,
    estimatedHours: cleaned.estimatedHours || ROUTE_CONFIG.OPTIMIZATION.DEFAULT_SERVICE_TIME_MINUTES / 60,
    frequency: cleaned.frequency || 'On-Demand'
  };
};

/**
 * Check if route needs optimization
 */
export const routeNeedsOptimization = (route) => {
  if (!route.lastOptimized) return true;
  
  const hoursSinceOptimization = (Date.now() - new Date(route.lastOptimized)) / (1000 * 60 * 60);
  return hoursSinceOptimization > 24; // Re-optimize if older than 24 hours
};
